<template>
  <div>
    <page-title-bar :customTitle="customTitle"></page-title-bar>
    <v-container fluid grid-list-xl pt-0>
      <v-layout row wrap justify-center>
        <v-flex xs12>
          <v-toolbar :elevation="0" height="auto">
            <v-layout row justify-start align-center style="padding: 32px">
              <v-flex xs12 md3>
                <v-select v-model="chosen_year" :items="yearOptions" label="Chọn năm" :disabled="loader"
                  prepend-icon="event" hide-details></v-select>
              </v-flex>

              <v-flex xs12 md9 class="text-right">
                 <v-btn color="success" @click="exportExcel" :loading="exporting" :disabled="loader || !months.length">
                    <v-icon left small>cloud_download</v-icon>
                    Xuất Excel
                 </v-btn>
              </v-flex>
            </v-layout>
          </v-toolbar>

          <div class="mt-3">
            <v-card>
              <v-card-title class="headline">
                Báo cáo tình hình tài chính năm {{ chosen_year }}
              </v-card-title>
              <v-divider></v-divider>

              <v-card-text>
                <div v-if="months.length === 0 && !loader" class="text-center pa-4">
                  <v-icon large color="grey">info</v-icon>
                  <p class="mt-2">Chưa có báo cáo tháng nào được chốt trong năm {{ chosen_year }}</p>
                </div>

                <div v-else class="table-responsive">
                  <table class="yearly-report-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="min-width: 250px">Chỉ tiêu</th>
                        <th v-for="month in months" :key="month.month" class="text-right" style="min-width: 150px">
                          {{ formatMonthHeader(month.month) }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- TÀI SẢN -->
                      <tr class="section-header">
                        <td :colspan="months.length + 1"><strong>TÀI SẢN</strong></td>
                      </tr>
                      <tr>
                        <td>Tiền mặt</td>
                        <td v-for="month in months" :key="`tien_mat_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.tien_mat) }}
                        </td>
                      </tr>
                      
                      <!-- Ngân hàng - Expandable -->
                      <tr class="expandable-row" @click="toggleBankDetail">
                        <td>
                          Ngân hàng
                          <v-icon small class="ml-1">{{ showBankDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`ngan_hang_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.ngan_hang) }}
                        </td>
                      </tr>
                      <!-- Bank Detail Rows -->
                      <template v-if="showBankDetail">
                        <tr v-for="(bank, idx) in bankDetailRows" :key="`bank_detail_${idx}`" class="sub-row">
                          <td class="pl-4">- {{ bank.text }}</td>
                          <td v-for="month in months" :key="`bank_${idx}_${month.month}`" class="text-right">
                            {{ formatFinancialValue(getBankAmount(month, bank.account_code)) }}
                          </td>
                        </tr>
                      </template>
                      
                      <!-- Phải thu khách hàng - Expandable -->
                      <tr class="expandable-row" @click="toggleCustomerDebtDetail">
                        <td>
                          Phải thu khách hàng
                          <v-icon small class="ml-1">{{ showCustomerDebtDetail ? 'expand_less' : 'expand_more' }}</v-icon>
                        </td>
                        <td v-for="month in months" :key="`phai_thu_kh_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.phai_thu_khach_hang) }}
                        </td>
                      </tr>
                      <!-- Customer Debt Detail Rows -->
                      <template v-if="showCustomerDebtDetail">
                        <tr v-for="(room, idx) in customerDebtDetailRows" :key="`room_detail_${idx}`" class="sub-row">
                          <td class="pl-4">- {{ room.room_name }}</td>
                          <td v-for="month in months" :key="`room_${idx}_${month.month}`" class="text-right">
                            {{ formatFinancialValue(getRoomDebtAmount(month, room.room_name)) }}
                          </td>
                        </tr>
                        <tr class="sub-row">
                          <td class="pl-4">- Các công nợ nhỏ khác</td>
                          <td v-for="month in months" :key="`cong_no_nho_${month.month}`" class="text-right">
                            {{ formatFinancialValue(month.report_data.cong_no_nho_khac) }}
                          </td>
                        </tr>
                      </template>
                      
                      <tr>
                        <td>Công nợ nhà trọ</td>
                        <td v-for="month in months" :key="`cong_no_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.cong_no_nha_tro) }}
                        </td>
                      </tr>
                      <tr>
                        <td>Phải thu anh Long</td>
                        <td v-for="month in months" :key="`phai_thu_long_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.phai_thu_anh_long) }}
                        </td>
                      </tr>
                      <tr>
                        <td>Phải thu khác</td>
                        <td v-for="month in months" :key="`phai_thu_khac_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.phai_thu_khac) }}
                        </td>
                      </tr>
                      <tr class="total-row">
                        <td><strong>TỔNG CỘNG TÀI SẢN</strong></td>
                        <td v-for="month in months" :key="`tong_ts_${month.month}`" class="text-right">
                          <strong>{{ formatFinancialValue(month.report_data.tong_cong_tai_san) }}</strong>
                        </td>
                      </tr>

                      <!-- NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU -->
                      <tr class="section-header">
                        <td :colspan="months.length + 1"><strong>NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU</strong></td>
                      </tr>
                      <tr>
                        <td>Nợ dài hạn</td>
                        <td v-for="month in months" :key="`no_dai_han_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.no_dai_han) }}
                        </td>
                      </tr>
                      <tr class="sub-row">
                        <td class="pl-4">- Nợ phải trả</td>
                        <td v-for="month in months" :key="`no_phai_tra_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.no_phai_tra) }}
                        </td>
                      </tr>
                      <tr class="sub-row">
                        <td class="pl-4">- Vay</td>
                        <td v-for="month in months" :key="`vay_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.vay) }}
                        </td>
                      </tr>
                      <tr>
                        <td>LNCPP</td>
                        <td v-for="month in months" :key="`lncpp_${month.month}`" class="text-right">
                          {{ formatFinancialValue(month.report_data.lncpp) }}
                        </td>
                      </tr>
                      <tr class="total-row">
                        <td><strong>TỔNG CỘNG NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU</strong></td>
                        <td v-for="month in months" :key="`tong_no_${month.month}`" class="text-right">
                          <strong>{{ formatFinancialValue(month.report_data.tong_cong_no_phai_tra_va_von_chu_so_huu) }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="loader" class="text-center pa-4">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import api from "../../api";
import { ExcelExporter } from "../../helpers/ExcelExporter";
import swal from "sweetalert2";

export default {
  name: "BaoCaoTinhHinhTaiChinhCaNam",
  data() {
    return {
      loader: false,
      exporting: false,
      months: [],
      chosen_year: new Date().getFullYear().toString(),
      // Expand/collapse state - mặc định là đóng
      showBankDetail: false,
      showCustomerDebtDetail: false,
    };
  },
  watch: {
    chosen_year: function () {
      this.getData();
    },
  },
  computed: {
    customTitle() {
      return "Báo cáo tình hình tài chính cả năm";
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = 2025; y <= currentYear; y++) {
        years.push({ text: y.toString(), value: y.toString() });
      }
      return years;
    },
    // Lấy danh sách ngân hàng từ tháng đầu tiên (hoặc tháng nào có dữ liệu)
    bankDetailRows() {
      if (!this.months || this.months.length === 0) return [];
      // Tìm tháng có ngan_hang_detail
      for (const month of this.months) {
        if (month.report_data && month.report_data.ngan_hang_detail && month.report_data.ngan_hang_detail.length > 0) {
          return month.report_data.ngan_hang_detail;
        }
      }
      return [];
    },
    // Lấy danh sách phòng nợ unique từ tất cả các tháng
    customerDebtDetailRows() {
      if (!this.months || this.months.length === 0) return [];
      const roomMap = new Map();
      for (const month of this.months) {
        if (month.report_data && month.report_data.phai_thu_khach_hang_detail) {
          for (const room of month.report_data.phai_thu_khach_hang_detail) {
            if (!roomMap.has(room.room_name)) {
              roomMap.set(room.room_name, room);
            }
          }
        }
      }
      return Array.from(roomMap.values());
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    formatMonthHeader(month) {
      return moment(month).format("MM/YYYY");
    },
    toggleBankDetail() {
      this.showBankDetail = !this.showBankDetail;
    },
    toggleCustomerDebtDetail() {
      this.showCustomerDebtDetail = !this.showCustomerDebtDetail;
    },
    formatFinancialValue(val) {
      if (val === null || val === undefined) return '-';
      const num = Number(val);
      if (num === 0) return '0';
      if (num < 0) return `(${Math.abs(num).toLocaleString('vi-VN')})`;
      return num.toLocaleString('vi-VN');
    },
    // Lấy số tiền của ngân hàng theo account_code cho từng tháng
    getBankAmount(month, accountCode) {
      if (!month.report_data || !month.report_data.ngan_hang_detail) return 0;
      const bank = month.report_data.ngan_hang_detail.find(b => b.account_code === accountCode);
      return bank ? bank.amount : 0;
    },
    // Lấy số tiền nợ của phòng theo room_name cho từng tháng
    getRoomDebtAmount(month, roomName) {
      if (!month.report_data || !month.report_data.phai_thu_khach_hang_detail) return 0;
      const room = month.report_data.phai_thu_khach_hang_detail.find(r => r.room_name === roomName);
      return room ? room.amount : 0;
    },
    async getData() {
      this.loader = true;

      try {
        const result = await api.getYearlyFinancialReport(`year=${this.chosen_year}`);

        if (result.status === 200) {
          this.months = result.data.months || [];
        } else {
          swal.fire("Lỗi", result.message || "Không thể tải báo cáo", "error");
        }
      } catch (error) {
        console.error("Error loading yearly report:", error);
        swal.fire("Lỗi", "Không thể tải báo cáo", "error");
      }

      this.loader = false;
    },
    async exportExcel() {
        if (!this.months || this.months.length === 0) return;
        this.exporting = true;
        try {
            // 1. Define Columns
            const columns = [
                { header: 'Chỉ tiêu', key: 'name', width: 35, style: { bold: true } }
            ];
            
            this.months.forEach(m => {
                columns.push({
                    header: this.formatMonthHeader(m.month),
                    key: m.month,
                    width: 18,
                    align: 'right',
                    format: 'currency'
                });
            });

            const data = [];

            // Helper to create a row
            // getValue: (month) => number
            const createRow = (name, getValue, style = {}) => {
                const row = { name, style };
                if (style.indent) row.outlineLevel = style.indent;
                this.months.forEach(m => {
                    const val = getValue(m);
                    row[m.month] = val;
                });
                data.push(row);
            };
            
            // Helper for simple field
            const createFieldRow = (name, fieldName, style = {}) => {
                createRow(name, (m) => m.report_data ? m.report_data[fieldName] : 0, style);
            };

            // --- ASSETS ---
            data.push({ name: 'TÀI SẢN', style: { bold: true, background: 'FFE3F2FD' } });
            
            // 1. Tien mat
            // 1. Tien mat
            createRow('Tiền mặt', (m) => m.report_data.tien_mat);
            
            // Bank
            createFieldRow('Ngân hàng', 'ngan_hang');
            // Bank Details
            // Use the computed bankDetailRows logic locally or access it?
            // Computed props are available on `this`.
            // Wait, bankDetailRows logic: finds first month with detail.
            // But what if different months have different banks?
            // The logic in computed prop `bankDetailRows` returns list from *first available month*.
            // This implies the list of banks is assumed static or we only show what's in that month.
            // I will use `this.bankDetailRows` to carry over the same logic for consistency.
            if (this.bankDetailRows) {
                this.bankDetailRows.forEach(bank => {
                     createRow(
                         bank.text,
                         (m) => this.getBankAmount(m, bank.account_code),
                         { indent: 1 }
                     );
                });
            }

            // Customer Debt
            createFieldRow('Phải thu khách hàng', 'phai_thu_khach_hang');
            // Room Details
            if (this.customerDebtDetailRows) {
                this.customerDebtDetailRows.forEach(room => {
                    createRow(
                        `Phòng ${room.room_name}`,
                        (m) => this.getRoomDebtAmount(m, room.room_name),
                        { indent: 1 }
                    );
                });
                // Small debts
                 createFieldRow('Các công nợ nhỏ khác', 'cong_no_nho_khac', { indent: 1 });
            }

            // Other Assets
            createFieldRow('Công nợ nhà trọ', 'cong_no_nha_tro');
            createFieldRow('Phải thu anh Long', 'phai_thu_anh_long');
            createFieldRow('Phải thu khác', 'phai_thu_khac');

            // Total Assets
            createFieldRow('TỔNG CỘNG TÀI SẢN', 'tong_cong_tai_san', { bold: true, background: 'FFF3E0' });


            // --- LIABILITIES ---
            data.push({ name: 'NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU', style: { bold: true, background: 'FFE3F2FD' } });

            createFieldRow('Nợ dài hạn', 'no_dai_han');
            createFieldRow('Nợ phải trả', 'no_phai_tra', { indent: 1 });
            createFieldRow('Vay', 'vay', { indent: 1 });

            createFieldRow('LNCPP (Lợi nhuận chưa phân phối)', 'lncpp');

            createFieldRow('TỔNG CỘNG NỢ PHẢI TRẢ VÀ VỐN CHỦ SỞ HỮU', 'tong_cong_no_phai_tra_va_von_chu_so_huu', { bold: true, background: 'FFF3E0' });

            await ExcelExporter.exportReport({
                title: 'BÁO CÁO TÌNH HÌNH TÀI CHÍNH',
                subtitle: `Năm ${this.chosen_year}`,
                columns: columns,
                data: data,
                fileName: `BaoCaoTaiChinh_${this.chosen_year}`
            });

        } catch (error) {
            console.error(error);
            swal.fire("Lỗi", "Không thể xuất file Excel", "error");
        }
        this.exporting = false;
    },
  },
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.yearly-report-table {
  width: 100%;
  border-collapse: collapse;
}

.yearly-report-table th,
.yearly-report-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.yearly-report-table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.yearly-report-table .section-header {
  background-color: #e3f2fd;
}

.yearly-report-table .total-row {
  background-color: #fff3e0;
}

.yearly-report-table .sub-row {
  background-color: #fafafa;
}

.yearly-report-table .expandable-row {
  cursor: pointer;
}

.yearly-report-table .expandable-row:hover {
  background-color: #f0f0f0;
}

.yearly-report-table tbody tr:hover {
  background-color: #fafafa;
}

.pl-4 {
  padding-left: 32px !important;
}
</style>
